import clsx from 'clsx';
import { Check, ChevronDown, LogOut, Menu, Moon, Search, Sun } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Navigate, NavLink, Outlet, useLocation, useMatch } from 'react-router-dom';
import { authApi } from '@/api/http';
import { isAwaitingDecision, isDemo, useActions, useClientRole, useProjects, useProposals, useTasks, useThreads, useWorkspace } from '@/api';
import type { Project } from '@/api/types';
import { clientCan, clientRedirect, clientSections, type ClientSection } from '@/lib/access';
import { health } from '@/lib/status';
import { openTour } from '@/lib/tour';
import { CommandPalette } from './CommandPalette';
import { NotificationBell } from './NotificationBell';
import { Tour } from './Tour';
import { Avatar } from './ui';

type Theme = 'light' | 'dark';

function useTheme(): [Theme, () => void] {
  const [theme, setTheme] = useState<Theme>(() => (document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light'));
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    try {
      localStorage.setItem('portal.theme', theme);
    } catch {
      /* not critical */
    }
  }, [theme]);
  return [theme, () => setTheme((t) => (t === 'dark' ? 'light' : 'dark'))];
}

/** The studio mark: «42» held by corner brackets, the one place the brand colour appears. */
export function Mark() {
  return (
    <span className="frame frame-sm grid size-8 place-items-center [--c:rgb(var(--brand))]" aria-hidden>
      <span className="text-[13px] font-bold tracking-tight">42</span>
    </span>
  );
}

const logout = () => authApi.logout().finally(() => window.location.assign('/login'));

/** Offer and privacy policy of the contour; nothing when its config has none. */
export function LegalLinks({ className }: { className?: string }) {
  const docs = useWorkspace().data?.contour.docs;
  const links = [
    docs?.offer && { href: docs.offer, label: 'Оферта' },
    docs?.privacy && { href: docs.privacy, label: 'Политика конфиденциальности' },
  ].filter(Boolean) as { href: string; label: string }[];
  if (!links.length) return null;
  return (
    <span className={className}>
      {links.map((l, i) => (
        <span key={l.href}>
          {i > 0 && ' · '}
          <a href={l.href} target="_blank" rel="noreferrer noopener" className="underline underline-offset-2 hover:text-ink">
            {l.label}
          </a>
        </span>
      ))}
    </span>
  );
}

const dot = { done: 'bg-done', waiting: 'bg-waiting', overdue: 'bg-overdue', active: 'bg-active', neutral: 'bg-faint' } as const;

function Count({ n }: { n?: number }) {
  if (!n) return null;
  return <span className="num ml-1.5 rounded-full bg-ink px-1.5 text-[10px] font-semibold leading-4 text-inverse">{n}</span>;
}

/**
 * Clients have one to a few projects. The switcher sits next to the client's
 * name like a breadcrumb: one click to jump between them, invisible when there
 * is only one project to be in.
 */
function ProjectSwitcher({ projects, currentId }: { projects: Project[]; currentId?: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { data: tasks } = useTasks();
  const { pathname } = useLocation();
  const current = projects.find((p) => p.id === currentId);
  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    const close = (e: MouseEvent) => ref.current && !ref.current.contains(e.target as Node) && setOpen(false);
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  return (
    <div ref={ref} className="relative min-w-0">
      <button
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="menu"
        className={clsx('flex h-8 w-full max-w-[280px] items-center gap-2 rounded px-2.5 text-sm transition-colors hover:bg-sunken', current ? 'font-medium' : 'text-muted')}
      >
        {current && <span className={clsx('size-1.5 shrink-0 rounded-full', dot[health[current.health].tone])} />}
        <span className="truncate">{current ? current.title : `Проекты, ${projects.length}`}</span>
        <ChevronDown className="size-3.5 shrink-0 text-muted" />
      </button>
      {open && (
        <div role="menu" className="absolute left-0 top-10 z-40 w-80 rounded-lg bg-canvas p-1.5 shadow-pop">
          {projects.map((p) => {
            const h = health[p.health];
            const waiting = tasks?.filter((t) => t.projectId === p.id && t.needsClient && t.status !== 'done').length ?? 0;
            return (
              <NavLink key={p.id} role="menuitem" to={`/p/${p.id}`} className="flex items-start gap-2.5 rounded px-2.5 py-2 hover:bg-sunken">
                <span className={clsx('mt-1.5 size-1.5 shrink-0 rounded-full', dot[h.tone])} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">{p.title}</span>
                  <span className="block text-xs text-muted">
                    {h.label}
                    {waiting > 0 && <span className="text-waiting">, ждём вас: {waiting}</span>}
                  </span>
                </span>
                {p.id === currentId && <Check className="mt-0.5 size-4 shrink-0" />}
              </NavLink>
            );
          })}
          <div className="my-1 h-px bg-line" />
          <NavLink to="/" role="menuitem" className="block rounded px-2.5 py-2 text-sm text-muted hover:bg-sunken hover:text-ink">
            Сводка по всем проектам
          </NavLink>
        </div>
      )}
    </div>
  );
}

function TopBar({ onSearch, projects }: { onSearch: () => void; projects?: Project[] }) {
  const { data: ws } = useWorkspace();
  const role = ws?.me.clientRole;
  const { data: actions } = useActions();
  const { data: threads } = useThreads(undefined, clientCan(role, 'work'));
  const { data: proposals } = useProposals(clientCan(role, 'work'));
  const [theme, toggle] = useTheme();
  const [menu, setMenu] = useState(false);
  const { pathname } = useLocation();
  const projectId = useMatch('/p/:projectId/*')?.params.projectId;
  useEffect(() => setMenu(false), [pathname]);

  const single = projects?.length === 1 ? projects[0] : undefined;
  // Only what the role reaches: an accountant sees money and documents, a member no money.
  const all: Record<ClientSection, { to: string; label: string; count?: number; end?: boolean }> = {
    home: single ? { to: `/p/${single.id}`, label: 'Проект' } : { to: '/', label: 'Сводка', end: true },
    inbox: { to: '/inbox', label: 'Сообщения', count: threads?.reduce((n, t) => n + t.unread, 0) },
    money: { to: '/money', label: 'Деньги', count: actions?.filter((a) => a.kind === 'invoice' || a.kind === 'act').length },
    proposals: { to: '/proposals', label: 'Предложения', count: proposals?.filter((p) => isAwaitingDecision(p)).length },
    documents: { to: '/documents', label: 'Документы' },
    access: { to: '/access', label: 'Доступы', count: actions?.filter((a) => a.kind === 'secret' || (a.kind === 'onboarding' && a.item.kind === 'access')).length },
  };
  const sections = ws ? clientSections(role).map((k) => ({ ...all[k], key: k })) : [];

  return (
    <header className="sticky top-0 z-30 border-b border-line bg-canvas/95 backdrop-blur">
      <div className="mx-auto flex h-14 max-w-[1240px] items-center gap-2 px-4 md:px-8">
        <NavLink to="/" className="flex shrink-0 items-center gap-3 pr-1" aria-label="На главную">
          <Mark />
          <span className="hidden text-sm font-semibold sm:block">{ws?.clientName ?? ' '}</span>
        </NavLink>
        {projects && projects.length > 1 && (
          <>
            <span className="text-faint" aria-hidden>
              /
            </span>
            <ProjectSwitcher projects={projects} currentId={projectId} />
          </>
        )}

        <nav className="ml-4 hidden h-full items-stretch gap-1 md:flex" aria-label="Разделы">
          {sections.map((s) => (
            <NavLink
              key={s.to}
              to={s.to}
              end={s.end}
              data-tour={`nav-${s.key}`}
              className={({ isActive }) =>
                clsx(
                  'relative flex items-center px-3 text-sm transition-colors',
                  isActive ? 'font-medium text-ink after:absolute after:inset-x-3 after:-bottom-px after:h-0.5 after:bg-ink' : 'text-muted hover:text-ink',
                )
              }
            >
              {s.label}
              <Count n={s.count} />
            </NavLink>
          ))}
        </nav>

        <div className="ml-auto flex shrink-0 items-center gap-2">
          <button onClick={onSearch} className="hidden h-8 w-52 items-center gap-2 rounded px-2.5 text-sm text-faint shadow-hair transition-colors hover:text-muted 2xl:flex">
            <Search className="size-4" />
            <span className="flex-1 text-left">Найти задачу, файл…</span>
            <span className="kbd">⌘K</span>
          </button>
          <button onClick={onSearch} className="btn-ghost size-8 px-0 2xl:hidden" aria-label="Поиск">
            <Search className="size-4" />
          </button>
          <button className="btn-ghost size-8 px-0" onClick={toggle} aria-label={theme === 'dark' ? 'Светлая тема' : 'Тёмная тема'}>
            {theme === 'dark' ? <Sun className="size-4" /> : <Moon className="size-4" />}
          </button>
          <NotificationBell />
          {ws && (
            <NavLink to="/profile" aria-label="Мой профиль" title="Мой профиль">
              <Avatar person={ws.me} />
            </NavLink>
          )}
          <button className="btn-ghost size-8 px-0 md:hidden" onClick={() => setMenu((m) => !m)} aria-label="Разделы" aria-expanded={menu}>
            <Menu className="size-4" />
          </button>
        </div>
      </div>
      {menu && (
        <nav className="border-t border-line px-4 py-2 md:hidden" aria-label="Разделы">
          {sections.map((s) => (
            <NavLink key={s.to} to={s.to} end={s.end} className={({ isActive }) => clsx('flex h-10 items-center text-sm', isActive ? 'font-medium' : 'text-muted')}>
              {s.label}
              <Count n={s.count} />
            </NavLink>
          ))}
          <NavLink to="/profile" className={({ isActive }) => clsx('flex h-10 items-center text-sm', isActive ? 'font-medium' : 'text-muted')}>
            Мой профиль
          </NavLink>
          <button className="flex h-10 w-full items-center text-left text-sm text-muted" onClick={() => (setMenu(false), openTour())}>
            Как пользоваться кабинетом
          </button>
          <NavLink to="/settings" className={({ isActive }) => clsx('flex h-10 items-center text-sm', isActive ? 'font-medium' : 'text-muted')}>
            Компания и команда
          </NavLink>
          {/* Logout must be reachable on a phone, where the footer is far below the content. */}
          {!isDemo && (
            <button className="flex h-10 w-full items-center gap-2 text-left text-sm text-muted" onClick={logout}>
              <LogOut className="size-4" /> Выйти
            </button>
          )}
        </nav>
      )}
    </header>
  );
}

export function Layout() {
  const [palette, setPalette] = useState(false);
  const { pathname } = useLocation();
  const role = useClientRole();
  const { data: projects } = useProjects(clientCan(role, 'work'));

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPalette((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const away = clientRedirect(role, pathname);
  if (away) return <Navigate to={away} replace />;
  // With a single project there is nothing to summarise: go straight into it.
  if (pathname === '/' && projects?.length === 1) return <Navigate to={`/p/${projects[0].id}`} replace />;

  return (
    <div className="min-h-app">
      <TopBar onSearch={() => setPalette(true)} projects={projects} />
      <main className="mx-auto max-w-[1240px] px-4 pb-24 pt-8 md:px-8">
        <Outlet />
      </main>
      {isDemo && (
        <p className="pb-6 text-center text-xs text-faint">
          Демо-данные. Изменения живут до перезагрузки страницы. <a href="/studio" className="underline underline-offset-2">Кабинет студии</a>
        </p>
      )}
      <p className="space-x-3 pb-6 text-center text-xs text-faint">
        <NavLink to="/settings" className="underline underline-offset-2 hover:text-ink">
          Компания и команда
        </NavLink>
        <button className="underline underline-offset-2 hover:text-ink" onClick={openTour}>
          Как пользоваться кабинетом
        </button>
        <LegalLinks />
        {!isDemo && (
          <button className="underline underline-offset-2" onClick={logout}>
            Выйти
          </button>
        )}
      </p>
      <CommandPalette open={palette} onClose={() => setPalette(false)} />
      <Tour />
    </div>
  );
}
